import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Outlet, useLoaderData, Link, useMatches } from '@remix-run/react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { getSubdomainFromRequest } from '#app/utils/subdomain.server'

export async function loader({ request }: LoaderFunctionArgs) {
	// Just extract and validate subdomain exists - don't query Convex here
	const subdomain = getSubdomainFromRequest(request)
	if (!subdomain) {
		throw new Response('Subdomain not found', { status: 404 })
	}

	return json({
		subdomain,
	})
}

export default function SubdomainLayout() {
	const { subdomain } = useLoaderData<typeof loader>()
	
	// Use standard Convex query - it will be live and won't cause repeated calls
	const multiSiteChurch = useQuery(
		api.multiSiteChurches.getBySubdomain,
		subdomain ? { subdomain } : 'skip'
	)
	
	const churches = useQuery(
		api.multiSiteChurches.getChurches,
		multiSiteChurch ? { multiSiteChurchId: multiSiteChurch._id } : 'skip'
	)
	
	const ministries = useQuery(
		api.multiSiteChurches.getMinistries,
		multiSiteChurch ? { multiSiteChurchId: multiSiteChurch._id } : 'skip'
	)

	// Show loading state while fetching
	if (!multiSiteChurch) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="text-lg text-gray-600">Loading...</div>
				</div>
			</div>
		)
	}

	// Show 404 if multi-site church not found
	if (multiSiteChurch === null) {
		throw new Response('Multi-site church not found', { status: 404 })
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
					<h1 className="text-2xl font-bold text-gray-900">
						{multiSiteChurch.name}
					</h1>
					<p className="mt-1 text-sm text-gray-500">
						{multiSiteChurch.description}
					</p>
				</div>
			</header>

			<main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
					<div className="bg-white shadow rounded-lg p-6">
						<h2 className="text-lg font-semibold text-gray-900 mb-4">
							Churches ({churches.length})
						</h2>
						<ul className="space-y-2">
							{(churches || []).map((church) => (
								<li key={church._id}>
									<span className="text-gray-900">{church.name}</span>
								</li>
							))}
						</ul>
					</div>

					<div className="bg-white shadow rounded-lg p-6">
						<h2 className="text-lg font-semibold text-gray-900 mb-4">
							Ministries ({ministries.length})
						</h2>
						<ul className="space-y-2">
							{(ministries || []).map((ministry) => (
								<li key={ministry._id}>
									<Link
										to={`/ministries/${ministry._id}`}
										className="text-indigo-600 hover:text-indigo-900"
									>
										{ministry.name}
									</Link>
								</li>
							))}
						</ul>
					</div>
				</div>

				<Outlet />
			</main>
		</div>
	)
}
